import { useCallback } from 'react';

import { ApolloError } from '@apollo/client';

import {
  useLoyaltyServiceUpdateIdAndPayMutation,
  useLoyaltyUpdateIdAndPayMutation,
} from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';

import { LoyaltyIdAndPay, UpdateLoyaltyIdAndPayInput } from './types';

const getResolvedApolloError = (result: unknown): ApolloError | undefined => {
  const resolvedResult = result as {
    error?: ApolloError;
    errors?: ReadonlyArray<unknown>;
  };

  if (resolvedResult.error) {
    return resolvedResult.error;
  }
  if (resolvedResult.errors?.length) {
    return new ApolloError({ graphQLErrors: resolvedResult.errors as any });
  }
  return undefined;
};

export const useUpdateIdAndPay = () => {
  const { loyaltyUserId } = useLoyaltyUserId();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const useService = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_ID_AND_PAY));
  const [executeLegacyMutation, legacyResult] = useLoyaltyUpdateIdAndPayMutation({
    errorPolicy: 'all',
  });
  const [executeServiceMutation, serviceResult] = useLoyaltyServiceUpdateIdAndPayMutation({
    errorPolicy: 'all',
  });

  const updateIdAndPay = useCallback(
    async (input: UpdateLoyaltyIdAndPayInput): Promise<LoyaltyIdAndPay> => {
      if (!loyaltyUserId) {
        throw new ApolloError({ errorMessage: 'A loyalty user is required to update ID & Pay' });
      }

      if (useService) {
        const result = await executeServiceMutation({ variables: { input } });
        const resolvedError = getResolvedApolloError(result);
        if (resolvedError) {
          throw resolvedError;
        }

        await refetchLoyaltyUser();
        return result.data?.loyaltyServiceUpdateIdAndPay ?? input;
      }

      const result = await executeLegacyMutation({
        variables: {
          idAndPayInput: input,
          loyaltyId: loyaltyUserId,
        },
      });
      const resolvedError = getResolvedApolloError(result);
      if (resolvedError) {
        throw resolvedError;
      }

      await refetchLoyaltyUser();
      return {
        ...input,
        enabled: result.data?.loyaltyUpdateIdAndPay?.enabled ?? input.enabled,
      };
    },
    [executeLegacyMutation, executeServiceMutation, loyaltyUserId, refetchLoyaltyUser, useService]
  );

  const selectedResult = useService ? serviceResult : legacyResult;

  return {
    error: selectedResult.error,
    loading: selectedResult.loading,
    updateIdAndPay,
  };
};
